/**
 * @license Copyright (c) 2014-2023, CKSource Holding sp. z o.o.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor.js';

import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment.js';
import Autoformat from '@ckeditor/ckeditor5-autoformat/src/autoformat.js';
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote.js';

import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold.js';
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic.js';
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline.js';
import Strikethrough from '@ckeditor/ckeditor5-basic-styles/src/strikethrough.js';
import Subscript from '@ckeditor/ckeditor5-basic-styles/src/subscript.js';
import Superscript from '@ckeditor/ckeditor5-basic-styles/src/superscript.js';

import CKFinder from '@ckeditor/ckeditor5-ckfinder/src/ckfinder.js';
import CKFinderUploadAdapter from '@ckeditor/ckeditor5-adapter-ckfinder/src/uploadadapter.js';

import CloudServices from '@ckeditor/ckeditor5-cloud-services/src/cloudservices.js';

import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials.js';

import FindAndReplace from '@ckeditor/ckeditor5-find-and-replace/src/findandreplace.js';

import FontBackgroundColor from '@ckeditor/ckeditor5-font/src/fontbackgroundcolor.js';
import FontColor from '@ckeditor/ckeditor5-font/src/fontcolor.js';
import FontFamily from '@ckeditor/ckeditor5-font/src/fontfamily.js';
import FontSize from '@ckeditor/ckeditor5-font/src/fontsize.js';

import GeneralHtmlSupport from '@ckeditor/ckeditor5-html-support/src/generalhtmlsupport.js';

import Heading from '@ckeditor/ckeditor5-heading/src/heading.js';

import Highlight from '@ckeditor/ckeditor5-highlight/src/highlight.js';

import HorizontalLine from '@ckeditor/ckeditor5-horizontal-line/src/horizontalline.js';

import Image from '@ckeditor/ckeditor5-image/src/image.js';
import ImageCaption from '@ckeditor/ckeditor5-image/src/imagecaption.js';
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle.js';
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar.js';
import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload.js';
import ImageResize from '@ckeditor/ckeditor5-image/src/imageresize.js';
import AutoImage from '@ckeditor/ckeditor5-image/src/autoimage.js';
import ImageInsert from '@ckeditor/ckeditor5-image/src/imageinsert.js';
import ImagePixelSize from './imagepixelsize.js';

import Indent from '@ckeditor/ckeditor5-indent/src/indent.js';

import Link from '@ckeditor/ckeditor5-link/src/link.js';

import List from '@ckeditor/ckeditor5-list/src/list.js';

import MathType from '@wiris/mathtype-ckeditor5';

import MediaEmbed from '@ckeditor/ckeditor5-media-embed/src/mediaembed.js';

import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph.js';

import PasteFromOffice from '@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice.js';

import SelectAll from '@ckeditor/ckeditor5-select-all/src/selectall.js';

import SourceEditing from '@ckeditor/ckeditor5-source-editing/src/sourceediting.js';

import SpecialCharacters from '@ckeditor/ckeditor5-special-characters/src/specialcharacters.js';
import SpecialCharactersArrows from '@ckeditor/ckeditor5-special-characters/src/specialcharactersarrows.js';
import SpecialCharactersCurrency from '@ckeditor/ckeditor5-special-characters/src/specialcharacterscurrency.js';
import SpecialCharactersEssentials from '@ckeditor/ckeditor5-special-characters/src/specialcharactersessentials.js';
import SpecialCharactersLatin from '@ckeditor/ckeditor5-special-characters/src/specialcharacterslatin.js';
import SpecialCharactersMathematical from '@ckeditor/ckeditor5-special-characters/src/specialcharactersmathematical.js';
import SpecialCharactersText from '@ckeditor/ckeditor5-special-characters/src/specialcharacterstext.js';

import StandardEditingMode from '@ckeditor/ckeditor5-restricted-editing/src/standardeditingmode.js';

import Style from '@ckeditor/ckeditor5-style/src/style.js';

import Table from '@ckeditor/ckeditor5-table/src/table.js';
import TableCaption from '@ckeditor/ckeditor5-table/src/tablecaption.js';
import TableCellProperties from '@ckeditor/ckeditor5-table/src/tablecellproperties.js';
import TableColumnResize from '@ckeditor/ckeditor5-table/src/tablecolumnresize.js';
import TableProperties from '@ckeditor/ckeditor5-table/src/tableproperties.js';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar.js';

import TextTransformation from '@ckeditor/ckeditor5-typing/src/texttransformation.js';

import WordCount from '@ckeditor/ckeditor5-word-count/src/wordcount.js';

/**
 * IMPORTANT
 * Required for MathType styles
 */
import '@wiris/mathtype-ckeditor5/dist/index.css';

class Editor extends ClassicEditor { }

/**
 * Plugins
 */
Editor.builtinPlugins = [
  Alignment,
  // Autoformat,
  AutoImage,
  BlockQuote,

  Bold,
  Italic,
  Underline,
  Strikethrough,
  Subscript,
  Superscript,

  CKFinder,
  CKFinderUploadAdapter,

  CloudServices,

  Essentials,

  FindAndReplace,

  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,

  GeneralHtmlSupport,

  Heading,

  Highlight,

  HorizontalLine,

  Image,
  ImageCaption,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  ImageResize,
  ImageInsert,
  ImagePixelSize,

  Indent,

  Link,

  List,

  MathType,

  MediaEmbed,

  Paragraph,

  PasteFromOffice,

  SelectAll,

  SourceEditing,

  SpecialCharacters,
  SpecialCharactersArrows,
  SpecialCharactersCurrency,
  SpecialCharactersEssentials,
  SpecialCharactersLatin,
  SpecialCharactersMathematical,
  SpecialCharactersText,

  StandardEditingMode,

  Style,

  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,

  // TextTransformation,

  WordCount
];

/**
 * Editor Config
 */
Editor.defaultConfig = {
  toolbar: {
    items: [
      'heading',
      '|',

      'bold',
      'italic',
      'underline',
      'strikethrough',
      'subscript',
      'superscript',

      '|',

      'fontSize',
      'fontFamily',
      'fontColor',
      'fontBackgroundColor',
      'highlight',

      '|',

      'alignment',

      '|',

      'bulletedList',
      'numberedList',

      '|',

      'outdent',
      'indent',

      '|',

      'link',

      'imageUpload',
      'insertTable',
      'mediaEmbed',
      'blockQuote',
      'horizontalLine',

      '|',

      'MathType',
      'ChemType',

      '|',

      'specialCharacters',

      '|',

      'findAndReplace',
      'selectAll',
      'sourceEditing',

      '|',

      'undo',
      'redo'
    ],
    shouldNotGroupWhenFull: true
  },

  language: 'en',

  /**
   * Image Configuration
   */
  image: {
    resizeUnit: '%',

    resizeOptions: [
      {
        name: 'resizeImage:original',
        label: 'Original',
        value: null
      },
      {
        name: 'resizeImage:25',
        label: '25%',
        value: '25'
      },
      {
        name: 'resizeImage:50',
        label: '50%',
        value: '50'
      },
      {
        name: 'resizeImage:75',
        label: '75%',
        value: '75'
      }
    ],

    toolbar: [
      'resizeImage',

      '|',

      'imageStyle:inline',
      'imageStyle:block',
      'imageStyle:side',

      '|',

      'toggleImageCaption',
      'imageTextAlternative'
    ]
  },

  /**
   * Table Configuration
   */
  table: {
    contentToolbar: [
      'tableColumn',
      'tableRow',
      'mergeTableCells',
      'tableCellProperties',
      'tableProperties'
    ]
  }
};

export default Editor;